<div class="agent-wizard-container">
  <div class="breadcrumb mb-3">
    <span>Conversational AI</span> >
    <span (click)="onExit()" class="cursor-pointer">Agents Catalogs</span>
    &nbsp;>&nbsp;
    <span class="fw-bold">{{
      isEditMode ? "Edit Agent" : "Create Agent"
    }}</span>
  </div>

  <div *ngIf="hasPendingCallsWarning" class="alert alert-warning d-flex align-items-center mb-3" role="alert">
    <span class="material-icons me-2">warning</span>
    <span>This agent has calls currently in queue. Publishing this agent and deleting it from the catalog are disabled until the queue is cleared.</span>
  </div>

  <div class="progress-indicator mb-4">
    <div class="d-flex align-items-center">
      <div
        class="step"
        [class.active]="currentStep >= 1"
        [class.completed]="currentStep > 1"
      >
        <div class="step-number">1</div>
        <div class="step-label">Agent Details</div>
      </div>
      <div class="step-line" [class.active]="currentStep >= 2"></div>
      <div
        class="step"
        [class.active]="currentStep >= 2"
        [class.completed]="currentStep > 2"
      >
        <div class="step-number">2</div>
        <div class="step-label">Studies</div>
      </div>
      <div class="step-line" [class.active]="currentStep >= 3"></div>
      <div class="step" [class.active]="currentStep >= 3">
        <div class="step-number">3</div>
        <div class="step-label">Questions</div>
      </div>
    </div>
  </div>

  <!-- Step 1: Agent Details -->
  <div class="wizard-content" *ngIf="!isLoading && currentStep === 1">
    <form [formGroup]="agentForm">
      <div class="form-group mb-3">
        <label for="agentName" class="form-label">Agent Name *</label>
        <input
          type="text"
          id="agentName"
          class="form-control"
          formControlName="name"
          placeholder="Enter agent name"
          [class.is-invalid]="
            agentForm.get('name')?.invalid && agentForm.get('name')?.touched
          "
        />
        <div
          class="invalid-feedback"
          *ngIf="
            agentForm.get('name')?.invalid && agentForm.get('name')?.touched
          "
        >
          Agent name is required
        </div>
      </div>

      <div class="form-group mb-3">
        <label for="agentDescription" class="form-label">Description</label>
        <textarea
          id="agentDescription"
          class="form-control"
          formControlName="description"
          rows="3"
          placeholder="Enter agent description"
        ></textarea>
      </div>
    </form>
  </div>

  <!-- Step 2: Studies -->
  <div class="wizard-content" *ngIf="!isLoading && currentStep === 2">
    <form [formGroup]="studiesForm">
      <div formArrayName="studies">
        <div
          *ngFor="let study of studiesFormArray.controls; let i = index"
          class="study-section mb-4"
          [formGroupName]="i"
        >
          <div
            class="study-header d-flex justify-content-between align-items-center mb-3"
          >
            <h5 class="mb-0">Study {{ i + 1 }}</h5>
            <div class="study-actions d-flex align-items-center gap-2">
              <button
                type="button"
                class="btn-remove"
                (click)="removeStudy(i)"
                *ngIf="studiesFormArray.length > 0"
              >
                <span class="material-icons">remove_circle</span>
              </button>
              <button
                type="button"
                class="btn-add"
                (click)="addStudy()"
                *ngIf="i === studiesFormArray.length - 1"
              >
                <span class="material-icons">add_circle</span>
              </button>
            </div>
          </div>

          <div class="study-content-wrapper d-flex">
            <div class="study-fields flex-grow-1">
              <div class="row mb-3">
                <div class="col-md-6">
                  <label class="form-label">Study *</label>
                  <div class="study-dropdown position-relative" [attr.data-study-index]="i">
                    <button
                      type="button"
                      class="form-select text-start study-dropdown-toggle"
                      (click)="toggleStudyDropdown(i, $event)"
                      [attr.aria-expanded]="!!studyDropdownOpen[i]"
                      aria-haspopup="listbox"
                      [class.is-invalid]="
                        study.get('study')?.invalid && study.get('study')?.touched
                      "
                    >
                      <span *ngIf="study.get('study')?.value">{{ study.get('study')?.value }}</span>
                      <span *ngIf="!study.get('study')?.value" class="text-muted">Select study</span>
                    </button>
                    <div
                      class="study-dropdown-menu"
                      *ngIf="studyDropdownOpen[i]"
                      role="listbox"
                      (click)="$event.stopPropagation()"
                      (keydown.escape)="studyDropdownOpen[i] = false"
                    >
                      <div class="study-dropdown-search p-2 border-bottom bg-white">
                        <input
                          type="text"
                          class="form-control form-control-sm"
                          placeholder="Search studies..."
                          [value]="studySearchText[i] || ''"
                          (input)="studySearchText[i] = $any($event.target).value"
                          (keydown.escape)="studyDropdownOpen[i] = false"
                          autocomplete="off"
                        />
                      </div>
                      <div class="study-dropdown-items">
                        <button
                          *ngFor="let studyOption of getAvailableStudies(i); trackBy: trackByStudyName"
                          type="button"
                          role="option"
                          class="dropdown-item study-dropdown-item"
                          (click)="selectStudy(i, studyOption.name)"
                        >
                          {{ studyOption.name }}
                        </button>
                        <div
                          *ngIf="getAvailableStudies(i).length === 0"
                          class="px-2 py-2 text-muted small"
                        >
                          No studies match
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    class="invalid-feedback d-block"
                    *ngIf="
                      study.get('study')?.invalid && study.get('study')?.touched
                    "
                  >
                    Study is required
                  </div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Priority <span *ngIf="study.get('enabled')?.value">*</span></label>
                  <select
                    class="form-select"
                    formControlName="priority"
                    [class.is-invalid]="
                      study.get('priority')?.invalid &&
                      study.get('priority')?.touched
                    "
                    [disabled]="!study.get('enabled')?.value"
                  >
                    <option value="">Select priority</option>
                    <option *ngFor="let p of getAvailablePriorities(i)" [value]="p">
                      {{ p.replace('P', '') }}
                    </option>
                  </select>
                  <div
                    class="invalid-feedback"
                    *ngIf="
                      study.get('priority')?.invalid &&
                      study.get('priority')?.touched
                    "
                  >
                    Priority is required
                  </div>
                </div>
              </div>
            </div>
            <div class="study-toggle-wrapper ms-4 d-flex align-items-end">
              <div class="status-toggle-container">
                <label class="form-label">Select Status</label>
                <div
                  class="toggle-wrapper d-flex align-items-center justify-content-between"
                >
                  <span class="toggle-label">Disable</span>
                  <mat-slide-toggle
                    formControlName="enabled"
                    color="primary"
                  ></mat-slide-toggle>
                  <span class="toggle-label">Enable</span>
                </div>
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label">Description</label>
            <textarea
              class="form-control"
              formControlName="description"
              rows="3"
              placeholder="Enter study description"
            ></textarea>
          </div>

          <div
            class="study-divider"
            *ngIf="i < studiesFormArray.length - 1"
          ></div>
        </div>
      </div>

      <div *ngIf="studiesFormArray.length === 0" class="text-center py-4">
        <p class="text-muted mb-3">No studies added yet</p>
        <button type="button" class="btn btn-primary" (click)="addStudy()">
          + Add Study
        </button>
      </div>

      <div *ngIf="studiesFormArray.length > 0" class="mt-3">
        <button
          type="button"
          class="btn btn-outline-primary"
          (click)="addStudy()"
        >
          + Add Study
        </button>
      </div>
    </form>
  </div>

  <!-- Step 3: Questions -->
  <div class="wizard-content" *ngIf="!isLoading && currentStep === 3">
    <div class="mb-4 d-flex flex-wrap justify-content-end gap-2">
      <button
        type="button"
        class="btn publish-btn"
        (click)="collapseAllQuestions()"
        [disabled]="!questionnaireInitialized"
      >
        Collapse All
      </button>
    </div>

    <div class="questionnaire-shell" #questionnaireHost>
      <div class="page-container">
        <main id="script-container" data-sortable-group="top-questions"></main>
      </div>


      <template id="question-template">
        <div class="question-card" data-type="question">
          <div class="question-header">
            <button class="collapse-btn">
              <span class="material-icons collapse-arrow">expand_more</span>
            </button>
            <span class="handle material-icons" title="Drag to reorder">drag_indicator</span>
            <span class="question-id-display" title="Question ID">Q1</span>
            <input
              type="text"
              class="question-title-input"
              placeholder="Question Displays here"
            />
            <select class="question-category">
              <option value="">Select a Category *</option>
            </select>
            <button class="delete-item-btn" title="Delete Question">
              <span class="material-icons">close</span>
            </button>
          </div>

          <div class="question-content">
            <textarea
              class="question-text"
              placeholder="Enter Question here..."
              rows="2"
            ></textarea>
            <textarea
              class="question-note"
              placeholder="Optional: Add a note (e.g., pronunciation guide)"
              rows="1"
            ></textarea>

            <div
              class="response-branch-container"
              data-sortable-group="branches"
            ></div>
            <div class="add-response-branch-btn-container">
              <button class="link-button link-blue add-response-branch-btn">
                + Add Response Condition
              </button>
            </div>
          </div>
        </div>
      </template>

      <template id="response-branch-template">
        <div class="response-branch-card" data-type="branch">
          <div class="branch-header">
            <div class="branch-header-left">
              <button class="collapse-branch-btn collapse-btn-inner">
                <span class="material-icons collapse-arrow">expand_more</span>
              </button>
              <span class="handle material-icons" title="Drag to reorder">drag_indicator</span>
              <label class="branch-header-label">If response is:</label>
              <input
                type="text"
                class="branch-condition"
                placeholder="e.g., Yes"
              />

              <div class="branch-status-toggle-wrapper">
                <label class="branch-status-toggle-label"
                  >Assign Statuses?</label
                >
                <input
                  type="checkbox"
                  class="status-mapping-toggle"
                  checked
                  hidden
                />
                <button type="button" class="status-toggle-button">
                  <span></span>
                </button>
              </div>
            </div>
            <button class="delete-item-btn" title="Delete Branch">
              <span class="material-icons">close</span>
            </button>
          </div>

          <div class="status-mapping-section">
            <h4 class="status-mapping-title">
              Status Mappings for this Condition
            </h4>
            <div class="status-grid">
              <div class="status-grid-hint">
                Select a status for each study. Default is 'GTS'.
              </div>
            </div>
          </div>

          <div
            class="follow-up-actions-container"
            data-sortable-group="actions"
          >
            <h4 class="follow-up-actions-title">Follow-up Actions</h4>
          </div>
          <div class="action-buttons-container">
            <button class="link-button link-blue add-action-question-btn">
              + Add Follow-up Question
            </button>
            <button class="link-button link-green add-action-statement-btn">
              + Add Statement
            </button>
          </div>
        </div>
      </template>

      <template id="statement-template">
        <div class="statement-card" data-type="statement">
          <div class="statement-inner">
            <span class="handle material-icons" title="Drag to reorder">drag_indicator</span>
            <div class="statement-fields">
              <div class="statement-top-row">
                <select class="statement-type">
                  <option value="statement">Say</option>
                  <option value="instruction">Instruction</option>
                  <option value="endCall">End Call</option>
                </select>
                <input
                  type="text"
                  class="statement-condition"
                  placeholder="Optional: sub-condition"
                />
              </div>
              <textarea
                class="statement-text"
                placeholder="Enter statement or instruction text..."
                rows="2"
              ></textarea>
            </div>
            <button class="delete-item-btn" title="Delete Statement">
              <span class="material-icons">close</span>
            </button>
          </div>
        </div>
      </template>
    </div>
    <div class="d-flex justify-content-center my-4">
      <button
        type="button"
        class="btn publish-btn"
        (click)="addQuestionFromToolbar()"
        [disabled]="!questionnaireInitialized"
      >
        Add New Question
      </button>
    </div>
  </div>

  <!-- Confirmation Modal - Available on all steps -->
  <div id="confirmation-modal">
    <div class="modal-panel">
      <h3 class="modal-title" id="confirmation-title">Confirm Action</h3>
      <p id="confirmation-message" class="modal-message">Are you sure?</p>
      <div class="modal-footer">
        <button id="confirm-delete-btn" class="btn btn-confirm-delete">
          Confirm
        </button>
        <button id="cancel-delete-btn" class="btn btn-cancel-delete">
          Cancel
        </button>
      </div>
    </div>
  </div>

  <!-- Alert Modal -->
  <div class="alert-modal" [class.active]="showAlertModal" (click)="closeAlertModal()">
    <div class="alert-modal-panel" (click)="$event.stopPropagation()">
      <div class="alert-modal-header">
        <h3 class="alert-modal-title">
          <div class="alert-icon-circle">
            <mat-icon class="alert-icon-inner">info</mat-icon>
          </div>
          {{ alertModalTitle }}
        </h3>
        <button class="alert-modal-close" (click)="closeAlertModal()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <p class="alert-modal-message">{{ alertModalMessage }}</p>
      <div class="alert-modal-footer">
        <button class="alert-modal-ok-btn" (click)="closeAlertModal()">
          OK
        </button>
      </div>
    </div>
  </div>

  <!-- Loading State -->
  <div class="loading-container" *ngIf="isLoading">
    <div class="text-center">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>

  <div class="action-buttons-container mt-4">
    <!-- Left side buttons -->
    <div class="action-buttons">
      <button
        type="button"
        class="btn btn-outline-secondary exit-btn"
        (click)="onExit()"
      >
        Exit
      </button>
      <button
        type="button"
        class="btn btn-outline-secondary back-btn"
        (click)="onGoBack()"
        *ngIf="currentStep > 1"
      >
        Go Back
      </button>
      <button
        type="button"
        class="btn btn-primary next-btn"
        (click)="currentStep === 3 ? onSaveQuestionnaire() : onNext()"
        [disabled]="
          (currentStep === 1 && agentForm.invalid) ||
          (currentStep === 2 && !isStudiesFormValid())
        "
      >
        {{ currentStep === 3 ? "Save" : "Next" }}
      </button>
    </div>

    <!-- Right side buttons (Build and Publish) - Only on Step 3 -->
    <div class="build-publish-container" *ngIf="currentStep === 3">
      <div class="build-publish-buttons">
        <div class="build-publish-group">
          <button
            type="button"
            class="btn btn-outline-primary build-btn"
            (click)="onBuild()"
            [disabled]="isBuilding || isPollingBuildStatus"
          >
            <span class="spinner-border spinner-border-sm me-1" *ngIf="isBuilding || isPollingBuildStatus" role="status">
              <span class="visually-hidden">Loading...</span>
            </span>
            <span class="material-icons me-1" *ngIf="!isBuilding && !isPollingBuildStatus">refresh</span>
            Build V.{{ nextBuildVersion }}
          </button>
          <div class="build-info" *ngIf="lastBuildVersion">
            Last Build V.{{ lastBuildVersion }} – {{ lastBuildDate | date: 'MM/dd/yyyy' }}
          </div>
        </div>
        <div class="build-publish-group">
          <button
            type="button"
            class="btn btn-primary publish-btn"
            (click)="onPublish()"
            [disabled]="hasPendingCallsWarning || isPublishing || !currentBuildStatus || currentBuildStatus.Status !== 'COMPLETED'"
            [title]="(!currentBuildStatus || currentBuildStatus.Status !== 'COMPLETED') ? 'Please wait for build to complete before publishing' : ''"
          >
            <span class="spinner-border spinner-border-sm me-1" *ngIf="isPublishing" role="status">
              <span class="visually-hidden">Loading...</span>
            </span>
            <span *ngIf="!isPublishing">Publish V.{{ nextPublishVersion }}</span>
            <span *ngIf="isPublishing">Publishing...</span>
          </button>
          <div class="build-info" *ngIf="lastPublishVersion">
            Last Publish V.{{ lastPublishVersion }} – {{ lastPublishDate | date: 'MM/dd/yyyy' }}
          </div>
        </div>
      </div>

      <!-- Saved Version Display -->
      <div class="build-info saved-version-info" *ngIf="savedVersion !== null" style="margin-top: 12px; text-align: center;">
        <span style="color: #8f9bb3; font-size: 13px;">Saved Version: V.{{ savedVersion }}</span>
      </div>

      <!-- Build Status Indicator -->
      <div class="build-status-container" *ngIf="currentBuildStatus">
        <div class="build-status-indicator"
             [class.status-pending]="currentBuildStatus.Status === 'PENDING'"
             [class.status-in-progress]="currentBuildStatus.Status === 'IN_PROGRESS'"
             [class.status-completed]="currentBuildStatus.Status === 'COMPLETED'"
             [class.status-failed]="currentBuildStatus.Status === 'FAILED'">
          <div class="d-flex align-items-center gap-2">
            <div class="status-spinner" *ngIf="currentBuildStatus.Status === 'IN_PROGRESS'">
              <div class="spinner-border spinner-border-sm" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <span class="material-icons status-icon"
                  *ngIf="currentBuildStatus.Status === 'COMPLETED' || currentBuildStatus.Status === 'FAILED'">
              {{ currentBuildStatus.Status === 'COMPLETED' ? 'check_circle' : 'error' }}
            </span>
            <span class="build-status-text">
              <strong>Status:</strong>
              <span [class.status-pending-text]="currentBuildStatus.Status === 'PENDING'"
                    [class.status-in-progress-text]="currentBuildStatus.Status === 'IN_PROGRESS'"
                    [class.status-completed-text]="currentBuildStatus.Status === 'COMPLETED'"
                    [class.status-failed-text]="currentBuildStatus.Status === 'FAILED'">
                {{ currentBuildStatus.Status === 'PENDING' ? 'PENDING' :
                   currentBuildStatus.Status === 'IN_PROGRESS' ? 'IN PROGRESS' :
                   currentBuildStatus.Status }}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Build Popup Modal -->
  <div class="build-popup-overlay" *ngIf="showBuildPopup" (click)="closeBuildPopup()">
    <div class="build-popup-content" (click)="$event.stopPropagation()">
      <div class="build-popup-header">
        <h5 class="build-popup-title">
          <span class="material-icons me-2" [class.success-icon]="buildPopupVersion" [class.error-icon]="!buildPopupVersion">
            {{ buildPopupVersion ? 'check_circle' : 'error' }}
          </span>
          {{ isPublishPopup ? 'Build Published' : (buildPopupVersion ? 'Build Started' : 'Error') }}
        </h5>
        <button type="button" class="build-popup-close" (click)="closeBuildPopup()">
          <span class="material-icons">close</span>
        </button>
      </div>
      <div class="build-popup-body">
        <p class="build-popup-message" [class.text-danger]="!buildPopupVersion">{{ buildPopupMessage }}</p>
        <div *ngIf="buildPopupVersion" class="build-popup-info">
          <p class="mb-0"><strong>{{ isPublishPopup ? 'Published Build Version' : 'Build Version' }}:</strong> V.{{ buildPopupVersion }}</p>
          <p class="mb-0" *ngIf="!isPublishPopup">
            <strong>Status:</strong>
            <span [class.status-pending]="(currentBuildStatus?.Status === 'PENDING' && currentBuildStatus?.BuildVersion === buildPopupVersion) || (!currentBuildStatus || currentBuildStatus.BuildVersion !== buildPopupVersion)"
                  [class.status-in-progress]="currentBuildStatus?.Status === 'IN_PROGRESS' && currentBuildStatus?.BuildVersion === buildPopupVersion"
                  [class.status-completed]="currentBuildStatus?.Status === 'COMPLETED' && currentBuildStatus?.BuildVersion === buildPopupVersion"
                  [class.status-failed]="currentBuildStatus?.Status === 'FAILED' && currentBuildStatus?.BuildVersion === buildPopupVersion">
              {{ (currentBuildStatus?.BuildVersion === buildPopupVersion ? currentBuildStatus?.Status : null) || 'PENDING' }}
            </span>
          </p>
          <p class="mb-0 text-muted small mt-2" *ngIf="!isPublishPopup && currentBuildStatus?.BuildVersion === buildPopupVersion && (currentBuildStatus?.Status === 'PENDING' || currentBuildStatus?.Status === 'IN_PROGRESS')">
            The build is running in the background and may take 5-30 minutes to complete.
          </p>
          <p class="mb-0 text-success small mt-2" *ngIf="!isPublishPopup && currentBuildStatus?.BuildVersion === buildPopupVersion && currentBuildStatus?.Status === 'COMPLETED'">
            Build completed successfully!
          </p>
          <p class="mb-0 text-danger small mt-2" *ngIf="!isPublishPopup && currentBuildStatus?.BuildVersion === buildPopupVersion && currentBuildStatus?.Status === 'FAILED'">
            Build failed. Please check the logs and try again.
          </p>
          <p class="mb-0 text-muted small mt-2" *ngIf="!isPublishPopup && (!currentBuildStatus || currentBuildStatus.BuildVersion !== buildPopupVersion)">
            The build is running in the background and may take 5-30 minutes to complete.
          </p>
          <p class="mb-0 text-success small mt-2" *ngIf="isPublishPopup">
            The build has been published and is now live.
          </p>
          <div *ngIf="!isPublishPopup && currentBuildStatus?.BuildLog" class="build-log mt-3">
            <strong>Build Log:</strong>
            <pre class="build-log-content">{{ currentBuildStatus?.BuildLog }}</pre>
          </div>
        </div>
      </div>
      <div class="build-popup-footer">
        <button type="button" class="btn btn-primary" (click)="closeBuildPopup()">
          OK
        </button>
      </div>
    </div>
  </div>

  <!-- Publish Confirmation Modal -->
  <div class="publish-confirm-modal" [class.active]="showPublishConfirmModal">
    <div class="publish-confirm-panel">
      <h3 class="publish-confirm-title">Do you want to Publish Agent?</h3>
      <p class="publish-confirm-message">
        Publishing it would allow you to assign it to pools.<br>Or<br>
        You can keep as draft for publish it later
      </p>
      <div class="publish-confirm-footer">
        <button class="btn-keep-draft" (click)="onPublishConfirmModalKeepDraft()">Keep as draft</button>
        <button class="btn-publish-agent" (click)="onPublishConfirmModalPublish()">Publish Agent</button>
      </div>
    </div>
  </div>
</div>
